import React from "react";
import { observer } from "mobx-react";
import { Button, Typography } from "@mui/material";
import { UploadIcon as UploadFile } from "../../../icons";
import { useRootStore } from "../../../store/contexts";

type Props = {
  onChange: (file: File) => void;
  acceptFileTypes?: string;
  strictAccept?: boolean;
  disabled?: boolean;
  label?: string;
};

// Extensiones declaradas en .accept del DSL, para mostrarlas en el botón. Los
// MIME types (application/pdf) se descartan: no le dicen nada al usuario.
const MAX_INLINE_EXTENSIONS = 4;

const parseExtensions = (accept?: string): string[] =>
  (accept ?? "")
    .split(",")
    .map((x) => x.trim())
    .filter((x) => x.startsWith("."));

export const LoadFileButton: React.FC<Props> = observer(
  ({ onChange, acceptFileTypes, strictAccept, disabled, label }) => {
    const rootStore = useRootStore();
    const { prefStore, langStore } = rootStore;
    const [fileTypeError, setFileTypeError] = React.useState<string | null>(
      null
    );

    const extensions = parseExtensions(acceptFileTypes);
    // Con muchas extensiones el label desbordaría: se recortan y el resto va al title.
    const extensionsHint =
      extensions.length === 0
        ? ""
        : extensions.length <= MAX_INLINE_EXTENSIONS
        ? ` (${extensions.join("/")})`
        : ` (${extensions.slice(0, MAX_INLINE_EXTENSIONS).join("/")} +${
            extensions.length - MAX_INLINE_EXTENSIONS
          })`;
    const extensionsTitle =
      extensions.length > MAX_INLINE_EXTENSIONS
        ? extensions.join(", ")
        : undefined;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        // El accept del input es solo una preferencia del diálogo: en macOS
        // el sistema deja elegir extensiones hermanas del mismo tipo (.mpeg
        // por .mp3). Con strict, la extensión se valida de verdad acá.
        if (strictAccept && extensions.length > 0) {
          const fileExtension =
            "." + (file.name.split(".").pop() ?? "").toLowerCase();
          if (!extensions.some((ext) => ext.toLowerCase() === fileExtension)) {
            setFileTypeError(
              langStore.invalidFileTypeMessage(extensions.join(", "))
            );
            event.target.value = "";
            return;
          }
        }
        setFileTypeError(null);
        onChange(file);
      }
      // Permite re-elegir el mismo archivo tras corregirlo (change no dispara
      // con value repetido).
      event.target.value = "";
    };

    return (
      <div>
        <label>
          <input
            style={{ display: "none" }}
            id="file-upload"
            name="upload-photo"
            accept={acceptFileTypes}
            onChange={handleFileChange}
            type="file"
          />
          <Button
            size={prefStore.toolbarSize}
            variant="outlined"
            startIcon={<UploadFile />}
            disabled={disabled === undefined ? false : disabled}
            title={extensionsTitle}
            component="span"
          >
            {(label === undefined ? "Crear uno nuevo" : label) + extensionsHint}
          </Button>{" "}
        </label>
        {fileTypeError && (
          <Typography color="error" sx={{ fontSize: 12, mt: 0.5 }}>
            {fileTypeError}
          </Typography>
        )}
      </div>
    );
  }
);
